// How the reader's ending went (`GameFacts.endingCase`): reads the scan
// into what each of the reader's ending moves was, and leaves the rule to
// `@chess/api-ending-case` (`classifyEnding`), where the cases live. A game
// with no ending has no case. The same pass records the kind of ending
// (`endingType`), by the material it was played with (the piece signature
// that lasts the most plies from the ending's start to the game's last
// position), how the ending's first position stood for the reader
// (`endingStart`) and the ending result from it and the outcome (`endingResult`; the rule is api-ending-case's
// `endingStartOf` and `endingResultOf`).
import type { GameFacts } from '@chess/api-model';
import { classifyEnding, endingResultOf, endingStartOf, endingTypeOfPositions, type EndingMove } from '@chess/api-ending-case';
import { capPawns, type FactExtractor, type ScannedGame } from '../extractor.ts';

/** Whether the best line from the position before this ply wins for the reader, who is to move there. */
function bestLineWins(game: ScannedGame, ply: number): boolean {
  const best = game.at(ply - 1)?.lines[0];
  if (!best) return false;
  if (best.score.mate !== undefined) return best.score.mate > 0;
  return capPawns((best.score.cp ?? 0) / 100) >= game.rules.winningPawns;
}

export const endingCase: FactExtractor = {
  id: 'ending-case',
  extract(game: ScannedGame, facts: GameFacts): void {
    const { fromPly, toPly } = game.range('ending');
    if (toPly <= fromPly) return;
    const readers: EndingMove[] = [];
    for (let ply = fromPly + 1; ply <= toPly; ply++) {
      if (!game.isReaders(ply)) continue;
      const move = game.moves[ply - 1];
      const missedWin = !!move && game.judge.kind(ply, move, game.reader) === 'miss' && bestLineWins(game, ply);
      readers.push({ before: game.pawnsAt(ply - 1), missedWin });
    }
    facts.endingCase = classifyEnding(facts.outcome, facts.ending, readers, game.rules.winningPawns);
    // The kind of ending: the material it was played with, over its positions to the game's last.
    const fens: string[] = [];
    for (let ply = fromPly; ply <= game.moves.length; ply++) {
      const fen = game.at(ply)?.fen;
      if (fen) fens.push(fen);
    }
    const type = endingTypeOfPositions(fens);
    if (type) facts.endingType = type;
    // How the ending started for the reader, and the result from there.
    const start = endingStartOf(game.pawnsAt(fromPly), game.rules.winningPawns);
    if (start) {
      facts.endingStart = start;
      facts.endingResult = endingResultOf(start, facts.outcome);
    }
  },
};
